import React, { useEffect, useMemo, useState } from "react";

import { Col, Row } from "antd";
import { Form, Formik } from "formik";
import capitalize from "lodash/capitalize";
import { Helmet } from "react-helmet";
import * as Yup from "yup";

import { Button } from "shared/components/Button";
import Container from "shared/components/Container";
import Collapse from "shared/components/Collapse";
import {
  Input,
  Select,
  VeroFormField,
  PasswordInput,
} from "shared/components/Form";
import { BackLink, UnderlineLink } from "shared/components/Links";
import { IntegrationProvider } from "shared/config/constants";
import { EntrataLogoLargeSvg, RealPageLogoLargeSvg } from "shared/icons";
import { RentManagerLogoLargeSvg } from "shared/icons/rent-manager";
import { YardiLogo } from "shared/images";
import { BackLinkProps } from "shared/interfaces/misc";

import EntrataTestConnection from "./EntrataTestConnection";
import { YARDI_INTERFACES } from "./shared/yardi.config";
import { StyledIntegrationLogin, YardiVersion } from "./styled";
import RentManagerTestConnection from "./RentManagerTestConnection";
import { useFetchLocations } from "manager/hooks/api";

// eslint-disable-next-line react/prop-types
const FormFieldShell = ({ children }) => (
  <Row gutter={[0, 24]} data-testid="form-field">
    <Col span={24}>{children}</Col>
  </Row>
);

// TODO: Move these interfaces to separate file

interface CredentialsType {
  endpointUrl?: string;
  serverName?: string;
  databaseName?: string;
  platform?: string;
  username?: string;
  password?: string;
  interfaceName?: string;
  inventoryProviderCompanyId?: string;
  inventoryProvider: string;
}

interface IntegrationLoginProps {
  submit: (values: CredentialsType, setSubmitting: any) => void;
  cancel: () => void;
  backLink: BackLinkProps;
  disabled?: boolean;
  editMode?: boolean;
  savedYardiCredentials?: CredentialsType;
  yardiPlatformTypes?: string[];
  yardiVersion?: string;
  isSubmitLoading?: boolean;
  provider: IntegrationProvider;
  connectionTested?: boolean;
  onTestConnection?: (endpointUrl: string) => void;
  isTestingConnection?: boolean;
  connectionTestResult?: {
    success: boolean;
    message: string;
  } | null;
}

const IntegrationLogin = ({
  provider,
  disabled,
  editMode,
  savedYardiCredentials,
  yardiPlatformTypes,
  submit,
  cancel,
  yardiVersion,
  isSubmitLoading,
  backLink,
  connectionTested: _connectionTested = false,
  onTestConnection,
  isTestingConnection = false,
  connectionTestResult = null,
}: IntegrationLoginProps) => {
  const [locationsLoading, setLocationsLoading] = useState<boolean | undefined>();
  const [locationsResult, setLocationsResult] = useState<{
    success: boolean;
    message: string;
    data: [];
  } | undefined>();
  const { fetchLocations } = useFetchLocations();
  const title = provider === IntegrationProvider.rentManager ? 'Connect to Rent Manager' : capitalize(provider);

  const subTitle = editMode
    ? `Please update ${title} credentials and sign in to continue`
    : `Please sign in with your ${title} credentials to continue`;
  const saveButton = useMemo(() => {
    if (provider === IntegrationProvider.entrata) {
      return "ENABLE INTEGRATION";
    }

    if (provider === IntegrationProvider.rentManager) {
      return "Confirm & Enable Integration";
    }

    if (editMode) {
      return "SAVE CHANGES";
    }
    return "SIGN IN";
  }, [editMode, provider]);

  useEffect(() => {
    // prefetch locations if in edit mode and rent manager and server name is provided
    if (editMode && provider === IntegrationProvider.rentManager && savedYardiCredentials?.serverName) {
      handleFetchLocations(savedYardiCredentials.serverName);
    }
  }, [editMode, provider, savedYardiCredentials?.serverName])

  // eslint-disable-next-line consistent-return
  const { Logo, ValidationSchema, initialValues } = useMemo(() => {
    // eslint-disable-next-line default-case
    switch (provider) {
      case IntegrationProvider.yardi:
        return {
          Logo: YardiLogo,
          ValidationSchema: Yup.object().shape({
            endpointUrl: Yup.string()
              .url("Company URL must be a valid URL")
              .required("Company URL is required"),
            serverName: Yup.string().required("Server name is required"),
            platform: Yup.string().required("Platform is required"),
            databaseName: Yup.string().required("Database name is required"),
            username: Yup.string().required("Username is required"),
            password: Yup.string().required("Password is required"),
            interfaceName: Yup.string().required("Interface name is required"),
          }),
          initialValues: {
            endpointUrl: savedYardiCredentials?.endpointUrl ?? "",
            serverName: savedYardiCredentials?.serverName ?? "",
            databaseName: savedYardiCredentials?.databaseName ?? "",
            platform: savedYardiCredentials?.platform ?? "",
            username: savedYardiCredentials?.username ?? "",
            password: savedYardiCredentials?.password ?? "",
            interfaceName: savedYardiCredentials?.interfaceName ?? "",
            inventoryProvider: provider,
          },
        };
      case IntegrationProvider.entrata:
        return {
          Logo: EntrataLogoLargeSvg,
          ValidationSchema: Yup.object().shape({
            endpointUrl: Yup.string()
              .url("Company URL must be a valid URL")
              .required("Company URL is required"),
            // No username/password validation for Entrata
          }),
          initialValues: {
            endpointUrl: savedYardiCredentials?.endpointUrl ?? "",
            // No username/password for Entrata
            inventoryProvider: provider,
          },
        };
      case IntegrationProvider.realPage:
        return {
          Logo: RealPageLogoLargeSvg,
          ValidationSchema: Yup.object().shape({
            inventoryProviderCompanyId:
              Yup.string().required("PMC ID is required"),
          }),
          initialValues: {
            inventoryProviderCompanyId:
              savedYardiCredentials?.inventoryProviderCompanyId ?? "",
            inventoryProvider: provider,
          },
        };
      case IntegrationProvider.rentManager:
        return {
          Logo: RentManagerLogoLargeSvg,
          ValidationSchema: Yup.object().shape({
            serverName: Yup.string().required("Company Code is required"),
            locationId: Yup.string().required("Location ID is required"),
          }),
          initialValues: {
            serverName: savedYardiCredentials?.serverName ?? "",
            inventoryProvider: provider,
            locationId: (savedYardiCredentials as any)?.locationId,
          },
        };
    }
  }, [provider, savedYardiCredentials]);


  const handleFetchLocations = async (serverName: string) => {
    if (provider !== IntegrationProvider.rentManager) {
      return;
    }

    setLocationsLoading(true);
    setLocationsResult(undefined);

    try {
      // Validate serverName is provided
      if (!serverName || serverName.trim() === "") {
        setLocationsResult({
          success: false,
          message:
            "Please enter a valid Company Code before testing the connection.",
          data: []
        });
        setLocationsLoading(false);
        return;
      }

      const result = await fetchLocations({
        companyCode: serverName,
      });

      // Backend now returns HTTP 200 with success field
      if (result?.success) {
        // Success case
        setLocationsResult({
          success: true,
          message: "Locations fetched successfully",
          data: result.data || [],
        });
        setLocationsLoading(false);
      } else {
        // Error case (success: false)
        setLocationsResult({
          success: false,
          message: result?.detail || "Failed to fetch locations",
          data: [],
        });
        setLocationsLoading(false);
      }
    } catch (error: any) {

      // Handle unexpected errors (network issues, etc.)
      if (error?.errors?.nonFieldErrors && Array.isArray(error.errors.nonFieldErrors)) {
        setLocationsResult({
          success: false,
          message: error.errors.nonFieldErrors[0] || "Failed to fetch locations",
          data: [],
        });
      } else if (error?.message) {
        setLocationsResult({
          success: false,
          message: error.message,
          data: [],
        });
      } else {
        setLocationsResult({
          success: false,
          message: "Failed to fetch locations. Please try again.",
          data: []
        });
      }
      setLocationsLoading(false);
    }
  };

  return (
    <Container expand noPadding>
      <StyledIntegrationLogin>
        <Helmet>
          <title>{title}</title>
        </Helmet>

        <Container
          className="yardi-login-content"
          noMobilePaddingX
          noPaddingTop
          maxWidth="xl"
        >
          {backLink && (
            <BackLink.Container className="back-link">
              <BackLink {...backLink} />
            </BackLink.Container>
          )}
          <img src={Logo} alt={provider} style={{ width: provider === IntegrationProvider.rentManager ? '70%' : undefined }} />
          <p className="subtitle">
            {provider === IntegrationProvider.entrata ? (
              <>
                Follow these two steps to connect Entrata with VERO:
                <br />
                Step 1: Test your Entrata connection
                <br />
                Step 2: Enable the integration
              </>
            ) : provider === IntegrationProvider.rentManager ? (
              <div style={{ maxWidth: '75%', margin: '0 auto' }}>
                Connect your Rent Manager account to automatically sync prospects, applications, and property data with VERO.
                <br />
                <br />
                You'll need your Company Code, which Rent Manager provides in your setup email.
              </div>
            ) : (
              subTitle
            )}
          </p>
          <Formik
            validationSchema={ValidationSchema}
            initialValues={initialValues}
            enableReinitialize
            validateOnMount
            onSubmit={(values, { setSubmitting }) => {
              submit(values, setSubmitting);
            }}
          >
            {({ submitForm, isSubmitting, values }) => (
              <Form>
                {provider === IntegrationProvider.rentManager ? (
                  <div>
                    <FormFieldShell>
                      <div>
                        <VeroFormField
                          id="serverName"
                          name="serverName"
                          label="Company Code"
                          type="text"
                          disabled={disabled}
                          as={Input}
                          placeholder="examplecompany-api"
                          applyStaticStyling={true}
                        />
                        {locationsResult?.success === false && <p
                          style={{
                            color: "red",
                            marginTop: "8px",
                            fontSize: "14px",
                            fontWeight: "400",
                          }}
                        >
                          ❌ {locationsResult?.message}
                        </p>}
                        <p
                          style={{
                            color: "#6b7280",
                            marginTop: "8px",
                            fontSize: "14px",
                            fontWeight: "400",
                          }}
                        >
                          You can find your Company Code at the start of your Rent Manager login URL (e.g. YourCompanyCode in https://YourCompanyCode.twa.rentmanager.com).
                        </p>
                      </div>
                    </FormFieldShell>

                    {/* show locations dropdown */}
                    {locationsResult?.success === true && (
                      locationsResult.data.length > 0 ? <FormFieldShell>
                        <VeroFormField
                          id="locationId"
                          name="locationId"
                          label="Select a Location"
                          required
                          as={Select}
                        >
                          {locationsResult?.data.map((location: any) => (
                            <Select.Option key={location.LocationID} value={location.LocationID}>
                              {location.FriendlyName} ({location.LocationID})
                            </Select.Option>
                          ))}
                        </VeroFormField>
                        <p style={{
                          color: "#6b7280",
                          marginTop: "8px",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                          {values.locationId ? (<>You've selected <b>{(locationsResult?.data.find((location: any) => location.LocationID === values.locationId) as any)?.FriendlyName}</b> for integration.</>) : "Select the location you want to connect to VERO. Each Location represents a separate database in Rent Manager."}
                        </p>

                        {/* Collapsible Help Section */}
                        <div style={{ marginTop: "16px" }}>
                          <Collapse
                            defaultCollapsed={true}
                            header={({ CollapseIcon, collapsed }) => (
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "12px",
                                backgroundColor: "#f9fafb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                color: "#374151"
                              }}>
                                <span style={{ marginRight: "8px" }}>🏢</span>
                                <div style={{ flex: 1 }}>
                                  <strong>Don't see the Location you need?</strong>
                                  {collapsed && (
                                    <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                                      It might not be shared with VERO yet.
                                    </div>
                                  )}
                                </div>
                                <CollapseIcon />
                              </div>
                            )}
                          >
                            <div style={{
                              padding: "16px",
                              backgroundColor: "#ffffff",
                              border: "1px solid #e5e7eb",
                              borderTop: "none",
                              borderBottomLeftRadius: "6px",
                              borderBottomRightRadius: "6px",
                            }}>
                              <p style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                marginBottom: "12px",
                                color: "#111827"
                              }}>
                                Add a missing Location in Rent Manager
                              </p>
                              <p style={{ fontSize: "14px", marginBottom: "12px", lineHeight: "1.6" }}>
                                To add it, open <strong>Rent Manager</strong> on your computer and:
                              </p>
                              <ol style={{
                                marginLeft: "20px",
                                marginBottom: "0",
                                fontSize: "14px",
                                lineHeight: "2",
                                paddingLeft: "0"
                              }}>
                                <li>Go to <strong>Admin → Available Integrations</strong>.</li>
                                <li>Find <strong>VERO</strong> in the list.</li>
                                <li>Click <strong>Request Activation</strong>.</li>
                                <li>In the popup, check the box for the Location you want to share.</li>
                                <li>Click <strong>Request Activation</strong> to save your changes.</li>
                              </ol>
                              <p style={{
                                fontSize: "14px",
                                marginTop: "12px",
                                marginBottom: "0",
                                lineHeight: "1.6"
                              }}>
                                Then come back here and refresh the list below.
                              </p>
                            </div>
                          </Collapse>
                        </div>
                      </FormFieldShell> : (
                        <FormFieldShell>
                          <p style={{ color: "red", marginTop: "8px", fontSize: "14px", fontWeight: "400" }}>⚠️ We couldn’t find any Locations for this Company Code.</p>
                          <div style={{
                            width: "100%",
                            marginBottom: "24px",
                            padding: "20px",
                            backgroundColor: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            color: "#374151",
                            boxSizing: "border-box",
                          }}>
                            <p style={{ marginBottom: "16px", fontSize: "14px", lineHeight: "1.6" }}>
                              This usually means your Rent Manager account hasn't shared any Locations with VERO yet.
                            </p>
                            <p style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "500" }}>
                              To fix this:
                            </p>
                            <ol style={{ marginLeft: "20px", marginBottom: "16px", fontSize: "14px", lineHeight: "2" }}>
                              <li>
                                Open <strong>Rent Manager</strong> on your computer.
                              </li>
                              <li>
                                Go to <strong>Admin → Available Integrations</strong>.
                              </li>
                              <li>
                                Find <strong>VERO</strong> and click <strong>Request Activation</strong>.
                              </li>
                              <li>
                                In the popup, check the boxes for the Locations you want to share.
                              </li>
                              <li>
                                Click <strong>Request Activation</strong> to save.
                              </li>
                            </ol>
                            <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                              After that, come back here and refresh the list below.
                            </p>
                          </div>
                        </FormFieldShell>
                      )
                    )}
                  </div>
                ) : provider !== IntegrationProvider.realPage ? (
                  <div>
                    {/* Entrata-specific instructions FIRST - Full Width */}
                    {provider === IntegrationProvider.entrata && (
                      <div
                        style={{
                          width: "100%",
                          marginBottom: "24px",
                          padding: "20px",
                          backgroundColor: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          color: "#374151",
                          boxSizing: "border-box",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 16px 0",
                            fontSize: "16px",
                            fontWeight: "400",
                          }}
                        >
                          <strong style={{ fontWeight: "600" }}>
                            Before you begin:
                          </strong>{" "}
                          Make sure VERO has been added as an API User in your
                          Entrata Admin Portal.
                        </p>
                        <ol
                          style={{
                            margin: "0",
                            paddingLeft: "20px",
                            fontSize: "16px",
                            lineHeight: "1.5",
                          }}
                        >
                          <li style={{ marginBottom: "8px" }}>
                            In Entrata Admin, go to{" "}
                            <strong style={{ fontWeight: "600" }}>
                              Apps → API Access
                            </strong>
                            .
                          </li>
                          <li style={{ marginBottom: "8px" }}>
                            Check the table of existing applications. If{" "}
                            <strong style={{ fontWeight: "600" }}>VERO</strong>{" "}
                            is listed, click{" "}
                            <strong style={{ fontWeight: "600" }}>
                              Manage
                            </strong>{" "}
                            and confirm <em>Web Service Permissions</em> are
                            enabled, then click{" "}
                            <strong style={{ fontWeight: "600" }}>Save</strong>.
                          </li>
                          <li>
                            If{" "}
                            <strong style={{ fontWeight: "600" }}>VERO</strong>{" "}
                            is not listed:
                            <ul
                              style={{
                                margin: "8px 0 0 20px",
                                padding: "0",
                                fontSize: "16px",
                              }}
                            >
                              <li style={{ marginBottom: "4px" }}>
                                Click{" "}
                                <strong style={{ fontWeight: "600" }}>
                                  Add API User
                                </strong>{" "}
                                (above the table, right side).
                              </li>
                              <li>
                                Search for{" "}
                                <strong style={{ fontWeight: "600" }}>
                                  VERO
                                </strong>
                                , click{" "}
                                <strong style={{ fontWeight: "600" }}>
                                  Enable Access
                                </strong>
                                , and follow the prompts.
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>
                    )}

                    {/* URL field comes AFTER instructions */}
                    <FormFieldShell>
                      {provider === IntegrationProvider.entrata ? (
                        <div>
                          <VeroFormField
                            id="endpointUrl"
                            name="endpointUrl"
                            label="Entrata Company URL"
                            type="url"
                            disabled={disabled}
                            as={Input}
                          />
                          <p
                            style={{
                              color: "#6b7280",
                              marginTop: "8px",
                              fontSize: "14px",
                              fontWeight: "400",
                            }}
                          >
                            Use the same URL your team uses to log into Entrata
                            (e.g. https://yourcompany.entrata.com).
                          </p>
                        </div>
                      ) : (
                        <VeroFormField
                          id="endpointUrl"
                          name="endpointUrl"
                          label="Company URL"
                          type="url"
                          disabled={disabled}
                          as={Input}
                        />
                      )}
                    </FormFieldShell>

                    {/* NOTE: find better way to handle forms for different providers */}
                    {IntegrationProvider.yardi === provider && (
                      <>
                        <FormFieldShell>
                          <VeroFormField
                            id="serverName"
                            name="serverName"
                            label="Server Name"
                            type="text"
                            disabled={disabled}
                            as={Input}
                          />
                        </FormFieldShell>
                        <FormFieldShell>
                          <VeroFormField
                            id="databaseName"
                            name="databaseName"
                            label="Database Name"
                            type="text"
                            disabled={disabled}
                            as={Input}
                          />
                        </FormFieldShell>
                        <FormFieldShell>
                          <VeroFormField
                            id="platform"
                            name="platform"
                            label="Platform"
                            disabled={disabled}
                            as={Select}
                          >
                            {yardiPlatformTypes?.map((yardiPlatform) => (
                              <Select.Option key={yardiPlatform}>
                                {`${yardiPlatform}`}
                              </Select.Option>
                            ))}
                          </VeroFormField>
                        </FormFieldShell>
                        <FormFieldShell>
                          <VeroFormField
                            id="interfaceName"
                            name="interfaceName"
                            label="Interface Name"
                            disabled={disabled}
                            as={Select}
                          >
                            {YARDI_INTERFACES.map((yardiInterface) => (
                              <Select.Option
                                key={yardiInterface.id}
                                value={yardiInterface.id}
                                data-testid={yardiInterface.id}
                              >
                                {yardiInterface.label}
                              </Select.Option>
                            ))}
                          </VeroFormField>
                        </FormFieldShell>
                      </>
                    )}

                    {/* Hide username/password fields for Entrata */}
                    {provider !== IntegrationProvider.entrata && (
                      <>
                        <FormFieldShell>
                          <VeroFormField
                            id="username"
                            name="username"
                            label="Username"
                            type="text"
                            disabled={disabled}
                            autoComplete="new-password"
                            as={Input}
                          />
                        </FormFieldShell>

                        <FormFieldShell>
                          <VeroFormField
                            id="password"
                            name="password"
                            label="Password"
                            disabled={disabled}
                            autoComplete="new-password"
                            as={PasswordInput}
                          />
                        </FormFieldShell>
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    <FormFieldShell>
                      <VeroFormField
                        id="inventoryProviderCompanyId"
                        name="inventoryProviderCompanyId"
                        label="PMC ID"
                        type="text"
                        disabled={disabled}
                        as={Input}
                      />
                    </FormFieldShell>
                  </div>
                )}

                {/* Test Connection component for Entrata */}
                {provider === IntegrationProvider.entrata && (
                  <EntrataTestConnection
                    onTestConnection={() =>
                      onTestConnection?.(values.endpointUrl)
                    }
                    isTestingConnection={isTestingConnection}
                    connectionTestResult={connectionTestResult}
                    disabled={isSubmitting || disabled}
                  />
                )}

                {provider === IntegrationProvider.rentManager && (
                  <><RentManagerTestConnection
                    onTestConnection={() =>
                      handleFetchLocations(values.serverName)
                    }
                    isTestingConnection={locationsLoading}
                    connectionTestResult={locationsResult}
                    disabled={isSubmitting || disabled || locationsLoading}
                  />
                  </>
                )}

                <Button
                  loading={isSubmitLoading}
                  type="primary"
                  data-testid="submit-btn"
                  className="submit-button"
                  disabled={
                    isSubmitting ||
                    disabled ||
                    (provider === IntegrationProvider.entrata &&
                      !_connectionTested) ||
                    (provider === IntegrationProvider.rentManager &&
                      (!values.locationId || !locationsResult?.success))
                  }
                  onClick={submitForm}
                >
                  {saveButton}
                </Button>
                {editMode && (
                  <div className="yardi-cancel-container">
                    <UnderlineLink disabled={isSubmitting} onClick={cancel}>
                      Cancel
                    </UnderlineLink>
                  </div>
                )}
                {yardiVersion && (
                  <YardiVersion>Version: {yardiVersion}</YardiVersion>
                )}
              </Form>
            )}
          </Formik>
        </Container>
      </StyledIntegrationLogin>
    </Container>
  );
};

export default IntegrationLogin;
